import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { ApiKey } from '@tilled-api-client';
import {
  COMMISSIONS_ROUTE,
  CUSTOMERS_ROUTE,
  DEVELOPERS_ROUTE,
  DISPUTES_ROUTE,
  DOCUMENTS_ROUTE,
  HOMEPAGE_ROUTE,
  INDEPENDENT_CREDITS_ROUTE,
  MERCHANTS_ROUTE,
  PAYMENTS_ROUTE,
  PAYOUTS_ROUTE,
  PRICING_TEMPLATES_ROUTE,
  REPORTS_ROUTE,
  SOFTWARE_PLATFORMS_ROUTE,
  TERMINALS_ROUTE,
} from 'app/core/constants';
import { AuthService } from 'app/core/services/auth.service';
import { Observable, filter, map, take } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class ScopesGuard {
  constructor(
    private authService: AuthService,
    private router: Router,
  ) {}
  canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> | boolean {
    return this.authService.user$.pipe(
      filter((user) => user != null),
      take(1),
      map(() => {
        // Checks if the user's role includes the scopes required for the route
        // If the route is not in the map, we assume the user has access to it
        const canScopeAccessRoute = this.authService.canScopeAccessRoute(state.url);
        if (canScopeAccessRoute.hasRequiredScope) {
          return true;
        } else {
          this.router.navigate(['/' + canScopeAccessRoute.redirectUrl]);
          return false;
        }
      }),
    );
  }
}

// Route to scope mapping (Used for checking if a user has access to a route)
// If the user has at least one of the scopes in the array, we can assume they have access to the route.
// For example, we won't prevent a user without the `request_logs:read` scope from accessing the `developers` route if they have `events:read`.
export const ROUTE_TO_SCOPES = new Map<string, ApiKey.ScopesEnum[]>([
  [HOMEPAGE_ROUTE, [ApiKey.ScopesEnum.BALANCE_TRANSACTIONS_READ]],
  [COMMISSIONS_ROUTE, [ApiKey.ScopesEnum.STATEMENTS_READ]],
  [CUSTOMERS_ROUTE, [ApiKey.ScopesEnum.CUSTOMERS_READ]],
  [
    DEVELOPERS_ROUTE,
    [
      ApiKey.ScopesEnum.API_KEYS_READ,
      ApiKey.ScopesEnum.EVENTS_READ,
      ApiKey.ScopesEnum.WEBHOOK_ENDPOINTS_READ,
      ApiKey.ScopesEnum.REQUEST_LOGS_READ,
    ],
  ],
  [DISPUTES_ROUTE, [ApiKey.ScopesEnum.DISPUTES_READ]],
  [DOCUMENTS_ROUTE, [ApiKey.ScopesEnum.ONBOARDING_READ]],
  [INDEPENDENT_CREDITS_ROUTE, [ApiKey.ScopesEnum.INDEPENDENT_CREDITS_READ]],
  [MERCHANTS_ROUTE, [ApiKey.ScopesEnum.ACCOUNTS_READ]],
  [PAYMENTS_ROUTE, [ApiKey.ScopesEnum.PAYMENT_INTENTS_READ]],
  [PAYOUTS_ROUTE, [ApiKey.ScopesEnum.PAYOUTS_READ]],
  [PRICING_TEMPLATES_ROUTE, [ApiKey.ScopesEnum.PRICING_TEMPLATES_READ]],
  [REPORTS_ROUTE, [ApiKey.ScopesEnum.REPORT_RUNS_READ]],
  [SOFTWARE_PLATFORMS_ROUTE, [ApiKey.ScopesEnum.ACCOUNTS_READ]],
  [TERMINALS_ROUTE, [ApiKey.ScopesEnum.TERMINAL_READERS_READ]],
]);
